import axios, {
    AxiosError,
    type AxiosInstance,
    type AxiosResponse,
    type InternalAxiosRequestConfig,
    type RawAxiosRequestConfig,
} from 'axios';
import type AbstractMiddleware from '@/services/middleware/AbstractMiddleware';

type Middleware = {
    name: string,
    middleware: AbstractMiddleware
};

export class HttpClient {
    protected readonly instance: AxiosInstance;

    private middleware: Array<Middleware> = [];

    constructor(middleware: Array<Middleware>) {
        this.instance = axios.create({
            baseURL: `${import.meta.env.VITE_FSUM_API_URL}`,
            withCredentials: true,
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
            },
        });

        this.setupMiddleware(middleware);
        this.setupRequestInterceptors();
        this.setupResponseInterceptors();
    }

    /**
     * Retrieve the current instance of the middleware if necessary
     * @param name middleware name key to retrieve
     * @returns middleware AxiosMiddleware
     */
    public getMiddleware = (name: string): AbstractMiddleware => {
        const middleware = this.middleware.find((clientMiddleware) => (
            name === clientMiddleware.name
        ))?.middleware;

        if (!middleware) {
            throw new Error(`middleware ${name} does not exist`);
        }

        return middleware;
    };

    private setupMiddleware = (clientMiddleware: Array<Middleware>) => {
        this.middleware = clientMiddleware;
        this.middleware.forEach(({ middleware }) => {
            // hook axios
            middleware.setup(this.instance);
        });
    };

    private setupRequestInterceptors = () => {
        this.instance.interceptors.request.use(
            this.requestHandler,
            this.errorHandler,
        );
    };

    private setupResponseInterceptors = () => {
        this.instance.interceptors.response.use(
            (response) => response.data,
            this.errorHandler,
        );
    };

    private requestHandler = (request: InternalAxiosRequestConfig): InternalAxiosRequestConfig => {
        // let any registered middleware handle the request first
        this.middleware.forEach(({ middleware }) => {
            /**
             * We currently assume that the canHandleRequest will be unique enough on
             * each class of type AxiosMiddleware.
             */
            if (middleware.isRequestMiddleware() && middleware.canHandleRequest(request)) {
                middleware.onRequest(request);
            }
        });

        return request;
    };

    private errorHandler = (error : AxiosError): Promise<AxiosResponse> => {
        // let any registered middleware handle errors first
        let resolved: Promise<AxiosResponse|undefined> | null = null;
        this.middleware.forEach(({ middleware }) => {
            /**
             * We currently assume that the canHandleError will be unique enough on
             * each class of type AxiosErrorMiddleware.
             */
            if (middleware.isErrorMiddleware() && middleware.canHandleError(error)) {
                resolved = middleware.onError(error);
            }
        });

        /**
         * Return the promise of the original request. This should look like
         * a slightly delayed request to the user as they will be refreshing
         * their access.
         */
        if (resolved) {
            return resolved;
        }

        return Promise.reject(error);
    };

    public get = <T>(url: string, config?: RawAxiosRequestConfig): Promise<T> => (
        this.instance.get(url, config)
    );

    public post = <T, D>(url: string, data?: D, config?: RawAxiosRequestConfig): Promise<T> => (
        this.instance.post(url, data, config)
    );

    public put = <T, D>(url: string, data?: D, config?: RawAxiosRequestConfig): Promise<T> => (
        this.instance.put(url, data, config)
    );

    public patch = <T, D>(url: string, data?: D): Promise<T> => this.instance.patch(url, data);

    public delete = <T>(url: string): Promise<T> => this.instance.delete(url);
}
